import { useState } from 'react';
import { FormattedNumber } from '../../../common/ui/FormattedNumber';

/**
 * Precio manual de una línea del carrito (comercial con el permiso "Cambiar precio en carrito",
 * pidiendo en nombre de un cliente): precio unitario y/o % de descuento 1, solo para ese pedido.
 * Con precio modificado la línea se pinta en rojo fuerte (lo hace la tarjeta con product.precioModificado)
 * y aquí se muestra la tarifa original tachada. "Restaurar tarifa" quita los manuales.
 */
export const PrecioManualForm = ({ product, onSave }) => {
  const [abierto, setAbierto] = useState(false);
  const [precio, setPrecio] = useState('');
  const [dto1, setDto1] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState(null);

  const abrir = () => {
    setError(null);
    setPrecio(product.precioManual !== null && product.precioManual !== undefined ? String(product.precioManual) : '');
    setDto1(product.descuento1Manual !== null && product.descuento1Manual !== undefined ? String(product.descuento1Manual) : '');
    setAbierto(true);
  };

  const guardar = async (datos) => {
    setGuardando(true);
    setError(null);
    try {
      await onSave(product, datos);
      setAbierto(false);
    } catch (e) {
      setError(e?.message || 'No se ha podido cambiar el precio');
    } finally {
      setGuardando(false);
    }
  };

  const enviar = (e) => {
    e.preventDefault();
    const p = precio === '' ? null : Number(String(precio).replace(',', '.'));
    const d = dto1 === '' ? null : Number(String(dto1).replace(',', '.'));
    if (p !== null && (Number.isNaN(p) || p < 0)) {
      setError('El precio debe ser un número mayor o igual que 0');
      return;
    }
    if (d !== null && (Number.isNaN(d) || d < 0 || d > 100)) {
      setError('El descuento debe estar entre 0 y 100');
      return;
    }
    if (p === null && d === null) {
      setError('Indica un precio o un descuento, o usa "Restaurar tarifa"');
      return;
    }
    guardar({ precioUnitario: p, descuento1: d });
  };

  if (!abierto) {
    return (
      <div className="d-flex flex-row align-items-center gap-2 mt-2 flex-wrap" data-testid="precio-manual-resumen">
        {product.precioModificado ? (
          <>
            <span className="badge bg-danger">Precio modificado por el comercial</span>
            <span className="small text-danger fw-bold">
              {product.precioManual !== null && product.precioManual !== undefined && (
                <>Precio <FormattedNumber value={product.precioManual} config={{ style: 'currency' }} /></>
              )}
              {product.descuento1Manual !== null && product.descuento1Manual !== undefined && (
                <> · dto. 1 {product.descuento1Manual}%</>
              )}
            </span>
            <span className="small text-muted">
              Tarifa: <s><FormattedNumber value={product.precioOriginal} config={{ style: 'currency' }} /></s>
              {product.descbc1Original ? <s> d1 {product.descbc1Original}%</s> : null}
            </span>
            <button type="button" className="btn btn-link btn-sm p-0" onClick={abrir}>Editar</button>
            <button type="button" className="btn btn-link btn-sm p-0 text-danger" disabled={guardando}
                    onClick={() => guardar({ precioUnitario: null, descuento1: null })}>
              Restaurar tarifa
            </button>
          </>
        ) : (
          <button type="button" className="btn btn-link btn-sm p-0" onClick={abrir}>Cambiar precio</button>
        )}
        {error && <span className="text-danger small">{error}</span>}
      </div>
    );
  }

  return (
    <form className="border border-danger rounded p-2 mt-2 bg-light" onSubmit={enviar} data-testid="precio-manual-form">
      <div className="small mb-2">
        <strong className="text-danger">Cambiar precio solo para este pedido.</strong>{' '}
        Tarifa actual: <FormattedNumber value={product.precioOriginal ?? product.basePrice} config={{ style: 'currency' }} />
        {product.descbc1Original ? ` con d1 ${product.descbc1Original}%` : ''}.
        Al aplicar un precio o un descuento fijo se anulan <strong>todos</strong> los demás descuentos y promociones de esta línea.
      </div>
      <div className="row g-2 align-items-end">
        <div className="col-6 col-md-4">
          <label className="form-label small mb-0" htmlFor={`precio-manual-${product.id}`}>Precio unitario (sin IVA)</label>
          <input id={`precio-manual-${product.id}`} type="number" min="0" step="0.01" className="form-control form-control-sm"
                 value={precio} onChange={(e) => setPrecio(e.target.value)} />
        </div>
        <div className="col-6 col-md-3">
          <label className="form-label small mb-0" htmlFor={`dto1-manual-${product.id}`}>Descuento 1 (%)</label>
          <input id={`dto1-manual-${product.id}`} type="number" min="0" max="100" step="0.01" className="form-control form-control-sm"
                 value={dto1} onChange={(e) => setDto1(e.target.value)} />
        </div>
      </div>
      {error && <div className="text-danger small mt-2">{error}</div>}
      <div className="d-flex gap-2 justify-content-end mt-2">
        <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setAbierto(false)} disabled={guardando}>
          Cancelar
        </button>
        {product.precioModificado && (
          <button type="button" className="btn btn-outline-danger btn-sm" disabled={guardando}
                  onClick={() => guardar({ precioUnitario: null, descuento1: null })}>
            Restaurar tarifa
          </button>
        )}
        <button type="submit" className="btn btn-danger btn-sm" disabled={guardando}>
          {guardando ? 'Guardando…' : 'Aplicar precio'}
        </button>
      </div>
    </form>
  );
};
